import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Clock } from "lucide-react";
import Reveal from "@/components/Reveal";
import Seo from "@/components/Seo";
import FinalCTA from "@/sections/FinalCTA";
import { getResourceBySlug, posts } from "@/lib/resources";

export default function ResourceDetailPage() {
 const { slug } = useParams();
 const resource = getResourceBySlug(slug);

 useEffect(() => {
 if (typeof window !== "undefined") {
   window.scrollTo({ top: 0, behavior: "instant" });
 }
 }, [slug]);

 if (!resource) {
 return (
 <div className="max-w-[900px] mx-auto px-5 py-32 text-center" data-testid="page-resource-notfound">
 <Seo title="Article not found — HG Corporates" description="The article you're looking for could not be found." path={`/resources/${slug || ""}`} noindex />
 <h1 className="font-display text-4xl font-black">Article not found</h1>
 <Link
 to="/resources"
 className="mt-6 inline-block text-[#0F1115] link-underline font-bold"
 >
 Back to all resources
 </Link>
 </div>
 );
 }

 // Suggest 3 other posts (excluding the current one)
 const related = posts.filter((p) => p.slug !== resource.slug).slice(0, 3);

 return (
 <div data-testid="page-resource-detail">
 <Seo
  title={resource.seoTitle || `${resource.title} | HG Corporates`}
  description={resource.seoDescription || resource.excerpt}
  path={`/resources/${resource.slug}`}
  type="article"
  image={resource.img}
  jsonLd={{
    "@context": "https://schema.org",
    "@type": "Article",
    headline: resource.title,
    description: resource.excerpt,
    image: resource.img,
    author: { "@type": "Person", name: "Bhavik Hariyani" },
    publisher: {
      "@type": "Organization",
      name: "HG Corporates",
      logo: {
        "@type": "ImageObject",
        url: "https://hgcorporates.com/brand/logo.png",
      },
    },
    mainEntityOfPage: `https://hgcorporates.com/resources/${resource.slug}`,
    articleSection: resource.tag,
  }}
  breadcrumbs={[
    { name: "Home", path: "/" },
    { name: "Resources", path: "/resources" },
    { name: resource.title, path: `/resources/${resource.slug}` },
  ]}
 />
 {/* Hero */}
 <section className="bg-white border-b border-[#EAEAEA]">
 <div className="max-w-[900px] mx-auto px-5 md:px-8 pt-14 md:pt-20 pb-10">
 <Reveal>
 <Link
 to="/resources"
 className="inline-flex items-center gap-1.5 text-[13px] font-bold text-[#4A4A4A] hover:text-[#0F1115] transition-colors"
 data-testid="resource-back-link"
 >
 <ArrowLeft size={14} /> All resources
 </Link>
 <div className="mt-6 inline-block text-[10px] uppercase tracking-[0.22em] font-bold text-[#0F1115] bg-[#FDD805] px-2.5 py-1 rounded-full">
 {resource.tag}
 </div>
 <h1 className="mt-4 font-display text-[36px] md:text-[52px] leading-[1.05] font-black tracking-tight text-[#0F1115]">
 {resource.title}
 </h1>
 <div className="mt-5 flex items-center gap-4 text-[13px] text-[#4A4A4A]">
 <div className="inline-flex items-center gap-1.5">
 <Clock size={13} /> {resource.read}
 </div>
 <div className="w-1 h-1 rounded-full bg-[#EAEAEA]" />
 <div>By Bhavik Hariyani - Founder, HG Corporates</div>
 </div>
 </Reveal>
 </div>
 <div className="max-w-[1080px] mx-auto px-5 md:px-8 pb-10">
 <Reveal delay={80}>
 <div className="rounded-3xl overflow-hidden border border-[#EAEAEA]">
 <img
 src={resource.img}
 alt=""
 className="w-full h-[280px] md:h-[420px] object-cover"
 />
 </div>
 </Reveal>
 </div>
 </section>

 {/* Body */}
 <section className="bg-white">
 <div className="max-w-[720px] mx-auto px-5 md:px-8 py-14 md:py-20">
 <Reveal>
 <p className="text-[19px] md:text-[21px] leading-[1.55] text-[#0F1115] font-medium">
 {resource.excerpt}
 </p>
 </Reveal>
 <div className="mt-10 space-y-10">
 {resource.sections.map((s, i) => (
 <Reveal key={s.h} delay={i * 60}>
 <div>
 <h2 className="font-display text-[24px] md:text-[28px] font-black text-[#0F1115] leading-tight">
 {s.h}
 </h2>
 <div className="mt-4 space-y-4">
 {s.p.map((block, k) => {
 if (typeof block === "string") {
 return (
 <p key={k} className="text-[16.5px] md:text-[17.5px] leading-[1.75] text-[#1C1F26]">
 {block}
 </p>
 );
 }
 if (block && block.h3) {
 return (
 <h3 key={k} className="font-display text-[18px] md:text-[20px] font-bold text-[#0F1115] pt-2">
 {block.h3}
 </h3>
 );
 }
 if (block && Array.isArray(block.list)) {
 return (
 <ul key={k} className="list-disc pl-6 space-y-2 text-[16.5px] md:text-[17.5px] leading-[1.7] text-[#1C1F26]">
 {block.list.map((item, li) => (
 <li key={li}>{item}</li>
 ))}
 </ul>
 );
 }
 if (block && block.quote) {
 return (
 <blockquote
 key={k}
 className="border-l-4 border-[#FDD805] bg-[#FFF9E6] pl-5 pr-4 py-3 text-[16.5px] md:text-[17.5px] leading-[1.7] text-[#0F1115] italic"
 >
 {block.quote}
 </blockquote>
 );
 }
 if (block && Array.isArray(block.flow)) {
 return (
 <div key={k} className="bg-[#F8F9FA] border border-[#EAEAEA] rounded-xl p-4 md:p-5 text-[15.5px] md:text-[16.5px] leading-[1.75] text-[#0F1115] space-y-1.5">
 {block.flow.map((step, si) => (
 <div key={si} className="flex items-start gap-2">
 <span className="text-[#FDD805] font-black mt-0.5">›</span>
 <span>{step}</span>
 </div>
 ))}
 </div>
 );
 }
 return null;
 })}
 </div>
 </div>
 </Reveal>
 ))}
 </div>
 </div>
 </section>

 {/* Related */}
 <section className="bg-[#F8F9FA] border-y border-[#EAEAEA]">
 <div className="max-w-[1280px] mx-auto px-5 md:px-8 py-16 md:py-20">
 <Reveal>
 <h2 className="font-display text-[28px] md:text-[36px] font-black text-[#0F1115]">
 Keep reading
 </h2>
 </Reveal>
 <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
 {related.map((p, i) => (
 <Reveal key={p.slug} delay={i * 80}>
 <Link
 to={`/resources/${p.slug}`}
 className="group block bg-white border border-[#EAEAEA] rounded-2xl overflow-hidden hover:border-[#0F1115] transition-colors h-full"
 data-testid={`related-resource-${p.slug}`}
 >
 <div className="aspect-[16/10] overflow-hidden">
 <img
 src={p.img}
 alt=""
 className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
 />
 </div>
 <div className="p-6">
 <div className="inline-block text-[10px] uppercase tracking-[0.22em] font-bold text-[#0F1115] bg-[#FEF6C7] px-2.5 py-1 rounded-full">
 {p.tag}
 </div>
 <h3 className="mt-4 font-display text-[18px] font-bold text-[#0F1115] leading-[1.25]">
 {p.title}
 </h3>
 <div className="mt-4 text-[12px] text-[#4A4A4A]">{p.read} read</div>
 </div>
 </Link>
 </Reveal>
 ))}
 </div>
 </div>
 </section>

 <FinalCTA />
 </div>
 );
}
